import React, { useState, useEffect, useRef } from "react";
import Loader from "../../loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { domain } from "../../../security";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";

const AddVehicles = ({ onClose, refreshData, VehicleToEdit }) => {
  const currentUserID = useSelector(selectUserID);

  const [formData, setFormData] = useState({
    vehicleName: "",
    vehicleType: "",
    plateNumber: "", // ← ADDED
  });

  const [isLoading, setIsLoading] = useState(false);
  const [plateStatus, setPlateStatus] = useState("idle"); // ← ADDED
  const plateCheckTimer = useRef(null); // ← ADDED

  useEffect(() => {
    if (VehicleToEdit) {
      setFormData({
        vehicleName:
          VehicleToEdit.vehicleName || VehicleToEdit.VehicleName || "",
        vehicleType:
          VehicleToEdit.vehicleType || VehicleToEdit.VehicleType || "",
        plateNumber:
          VehicleToEdit.plateNumber || VehicleToEdit.PlateNumber || "", // ← ADDED
      });

      const existingPlate =
        VehicleToEdit.plateNumber || VehicleToEdit.PlateNumber;
      setPlateStatus(existingPlate ? "available" : "idle");
    } else {
      setFormData({ vehicleName: "", vehicleType: "", plateNumber: "" });
      setPlateStatus("idle");
    }
  }, [VehicleToEdit]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // ← ADDED: Real-time plate number validation
  const handlePlateNumberChange = (e) => {
    const raw = e.target.value.toUpperCase().slice(0, 20);
    setFormData((prev) => ({ ...prev, plateNumber: raw }));

    if (!raw.trim()) {
      setPlateStatus("idle");
      clearTimeout(plateCheckTimer.current);
      return;
    }

    const existingPlate = (
      VehicleToEdit?.plateNumber ||
      VehicleToEdit?.PlateNumber ||
      ""
    ).toUpperCase();

    if (VehicleToEdit && raw.trim() === existingPlate) {
      setPlateStatus("available");
      return;
    }

    setPlateStatus("checking");
    clearTimeout(plateCheckTimer.current);
    plateCheckTimer.current = setTimeout(async () => {
      try {
        const excludeId = VehicleToEdit
          ? VehicleToEdit.id || VehicleToEdit.Id
          : null;
        const url = excludeId
          ? `${domain}/api/Vehicles/check-plate?plate=${encodeURIComponent(raw)}&excludeId=${excludeId}`
          : `${domain}/api/Vehicles/check-plate?plate=${encodeURIComponent(raw)}`;

        const res = await axios.get(url);
        setPlateStatus(res.data.exists ? "taken" : "available");
      } catch {
        setPlateStatus("idle");
      }
    }, 500);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // ← ADDED: Prevent submission if plate is invalid
    if (plateStatus === "taken") {
      toast.error("Plate number already exists. Please use a different one.");
      return;
    }
    if (plateStatus === "checking") {
      toast.info("Still checking plate number availability...");
      return;
    }

    setIsLoading(true);

    const apiUrl = `${domain}/api/Vehicles`;

    const payload = {
      VehicleName: formData.vehicleName,
      VehicleType: formData.vehicleType,
      PlateNumber: formData.plateNumber, // ← ADDED
      UserID: currentUserID,
    };

    try {
      if (VehicleToEdit) {
        const id = VehicleToEdit.id || VehicleToEdit.Id;
        await axios.put(`${apiUrl}/${id}`, payload);
        toast.success("Vehicle updated successfully");
      } else {
        await axios.post(apiUrl, payload);
        toast.success("Vehicle added successfully");
      }
      refreshData();
      onClose();
    } catch (error) {
      console.error("Validation Error Details:", error.response?.data?.errors);
      const serverMessage =
        error.response?.data?.title ||
        error.response?.data?.message ||
        "Check required fields";
      toast.error(`Error: ${serverMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ← ADDED: Badge Component for Plate Status
  const PlateBadge = () => {
    if (plateStatus === "idle") return null;
    if (plateStatus === "checking")
      return (
        <span className="text-xs text-slate-400 ml-2 animate-pulse">
          Checking…
        </span>
      );
    if (plateStatus === "available")
      return (
        <span className="text-xs text-green-600 ml-2 font-semibold">
          ✓ Available
        </span>
      );
    if (plateStatus === "taken")
      return (
        <span className="text-xs text-red-500 ml-2 font-semibold">✗ Taken</span>
      );
    return null;
  };

  return (
    <div className="p-6">
      {isLoading && <Loader />}
      <div className="relative w-full">
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 p-2 text-gray-500 hover:text-gray-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-2xl mb-6 font-bold text-gray-800">
          {VehicleToEdit ? "Edit Vehicle" : "Add Vehicle"}
        </h2>

        <form onSubmit={handleFormSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="vehicleName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Vehicle Name <span className="text-red-500">*</span>
            </label>
            <input
              id="vehicleName"
              type="text"
              required
              placeholder="Toyota, Honda, Ford, etc."
              className="border border-gray-300 rounded-md py-2 px-3 w-full focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              value={formData.vehicleName}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label
              htmlFor="vehicleType"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Vehicle Type
            </label>
            <select
              id="vehicleType"
              className="border border-gray-300 rounded-md py-2 px-3 w-full focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
              value={formData.vehicleType}
              onChange={handleInputChange}
            >
              <option value="">-- Select Type --</option>
              <option value="Sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="Truck">Truck</option>
              <option value="Van">Van</option>
              <option value="Motorcycle">Motorcycle</option>
              <option value="Bus">Bus</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* ← ADDED: Plate Number Field */}
          <div>
            <label
              htmlFor="plateNumber"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Plate Number <PlateBadge />
            </label>
            <input
              id="plateNumber"
              type="text"
              placeholder="e.g. ABC-1234"
              maxLength={20}
              className={`border rounded-md py-2 px-3 w-full outline-none uppercase font-mono tracking-widest transition-all ${
                plateStatus === "taken"
                  ? "border-red-400 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              }`}
              value={formData.plateNumber}
              onChange={handlePlateNumberChange}
            />
          </div>

          <button
            type="submit"
            disabled={plateStatus === "taken" || plateStatus === "checking"}
            className={`w-full font-semibold py-3 rounded-lg shadow transition-colors ${
              plateStatus === "taken" || plateStatus === "checking"
                ? "bg-indigo-300 text-white cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            {VehicleToEdit ? "Update Vehicle" : "Add Vehicle"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddVehicles;
